import { createAbortError } from "@/lib/unity/retry";

type InFlightLoaderInjection = {
  url: string;
  promise: Promise<void>;
};

let inFlightLoaderInjection: InFlightLoaderInjection | null = null;

export function resetInFlightUnityLoaderInjection(): void {
  inFlightLoaderInjection = null;
}

function isUnityLoaderReady(): boolean {
  return typeof window.createUnityInstance === "function";
}

function followSharedInjection(shared: Promise<void>, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      fn();
    };

    const onAbort = () => {
      queueMicrotask(() => settle(() => reject(createAbortError())));
    };

    signal?.addEventListener("abort", onAbort, { once: true });

    shared.then(
      () => {
        settle(() => {
          if (signal?.aborted) {
            reject(createAbortError());
            return;
          }
          resolve();
        });
      },
      (error) => {
        settle(() => {
          if (signal?.aborted) {
            reject(createAbortError());
            return;
          }
          reject(error);
        });
      }
    );
  });
}

function startUnityLoaderInjection(loaderUrl: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.crossOrigin = "anonymous";
    script.src = loaderUrl;
    script.async = true;
    script.dataset.playmathUnityLoader = "true";
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      fn();
    };

    const fail = (error: Error) => {
      script.remove();
      settle(() => reject(error));
    };

    script.onload = () => {
      if (isUnityLoaderReady()) {
        settle(() => resolve());
        return;
      }
      fail(new Error("Unity loader script loaded but createUnityInstance not available"));
    };

    script.onerror = () => {
      fail(new Error("Failed to load Unity loader script"));
    };

    document.body.appendChild(script);
  });
}

function getOrStartSharedInjection(loaderUrl: string): Promise<void> {
  if (inFlightLoaderInjection?.url === loaderUrl) {
    return inFlightLoaderInjection.promise;
  }

  const promise = startUnityLoaderInjection(loaderUrl);
  inFlightLoaderInjection = { url: loaderUrl, promise };
  void promise
    .catch(() => undefined)
    .finally(() => {
      if (inFlightLoaderInjection?.url === loaderUrl) {
        inFlightLoaderInjection = null;
      }
    });
  return promise;
}

/**
 * Injects the Unity loader script once per URL. The shared fetch is not bound
 * to any caller's AbortSignal — each consumer cancels only its own wait so one
 * unmount cannot strand another player (or a Strict Mode remount) on the
 * loading screen.
 */
export function injectUnityLoaderScript(loaderUrl: string, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  return followSharedInjection(getOrStartSharedInjection(loaderUrl), signal);
}
